import '../../AppHeader.css';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import '../../QuestionnaireForm.css';
import {
  Condition,
  ConditionConnector,
  FormItem,
  FormMetadata,
  isPageBreak,
  Operators,
  Option,
  Question,
  QuestionType,
} from '../../types';
import FormInput from './FormInput';
import FormSelect from './FormSelect';
import FormCheckbox from './FormCheckbox';
import FormRadioButton from './FormRadioButton';
import FormSubmitButton from './FormSubmitButton';
import { FormValidationError, updateFormData } from '../../api/AuthorizationApi';
import { getStoredUuidPathSegment } from '../../utils/urlUtils';
import { getTranslation } from '../../consts/translations';
import { isRTL, useLanguage } from '../../LanguageContext';
import FormScale from './FormScale';
import { validateMandatoryFormData } from '../../utils/formValidation';
import { getValidationMessage } from '../../utils/validationMessages';
import {
  getVolunteerNameMismatchError,
  isVolunteerNameQuestionKey,
  separatePatientIdFromNameLabel,
} from '../../utils/volunteerNameValidation';
import { renderSanitizedHtml } from '../../utils/sanitizeHtml';
import { applyFormulaCalculations, calculateFormula } from '../../utils/formulaCalculation';
import { datadogLogs } from '@datadog/browser-logs';

interface QuestionnaireFormProps {
  formData: FormMetadata;
  setFormData: (value: FormMetadata) => void;
  loadingForm: boolean;
  logout: () => void;
  isPreview?: boolean;
}

/** Split questions into pages at each PAGE_BREAK; page_break items are not included in any page. */
function splitQuestionsIntoPages(questions: FormItem[]): Question[][] {
  const pages: Question[][] = [];
  let current: Question[] = [];
  for (const q of questions) {
    if (isPageBreak(q)) {
      pages.push(current);
      current = [];
    } else {
      current.push(q);
    }
  }
  pages.push(current);
  return pages;
}

const QuestionnaireForm: React.FC<QuestionnaireFormProps> = ({
  formData,
  setFormData,
  loadingForm,
  logout,
  isPreview = false,
}) => {
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [isLoading, setIsLoading] = useState(false);
  const [showFinishedFormPage, setShowFinishedFormPage] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  const [currentPageIndex, setCurrentPageIndex] = useState(0);
  const { language } = useLanguage();
  const translation = getTranslation(language);
  const formDirection = isRTL(language) ? 'rtl' : 'ltr';
  const formDataRef = useRef(formData);
  const isReadOnlyRef = useRef(formData.form_filled);
  const hasSeededFormulasRef = useRef(false);
  const formDisabled = formData.form_filled;
  const showFormAfterFill = formData.show_form_after_fill ?? true;

  const [savingQuestions, setSavingQuestions] = useState<Record<string, boolean>>({});
  const [previewMessage, setPreviewMessage] = useState<string | null>(null);
  const [showDemoThankYou, setShowDemoThankYou] = useState(false);

  const isSavingInProgress = useMemo(
    () => Object.values(savingQuestions).some((v) => v),
    [savingQuestions],
  );

  const pages = useMemo(() => splitQuestionsIntoPages(formData.questions), [formData.questions]);
  const totalPages = pages.length;
  const isMultiPage = totalPages > 1;
  const questionsToRender = isMultiPage
    ? (pages[currentPageIndex] ?? [])
    : formData.questions.filter((q): q is Question => !isPageBreak(q));
  const formId = getStoredUuidPathSegment();

  useEffect(() => {
    formDataRef.current = formData;
  }, [formData]);

  useEffect(() => {
    isReadOnlyRef.current = formData.form_filled;
  }, [formData.form_filled]);

  useEffect(() => {
    hasSeededFormulasRef.current = false;
  }, [formId]);

  const getFormulaCalculationOptions = () => ({
    form_id: formId,
    onError: (details: {
      form_id?: string;
      question_key?: string;
      formula: string;
      error?: string;
    }) => {
      datadogLogs.logger.error('Formula calculation failed', details);
    },
  });

  const checkCondition = (
    conditions: Condition[] | undefined,
    allQuestions: Question[] = formData.questions.filter((q): q is Question => !isPageBreak(q)),
  ): boolean => {
    if (!conditions || conditions.length === 0) {
      return true;
    }

    let result: boolean | null = null;

    for (const condition of conditions) {
      const connectedQuestion = allQuestions.find(
        (q) => q.guid === condition.connected_question_guid,
      );

      if (
        !connectedQuestion ||
        !connectedQuestion.answer ||
        connectedQuestion.answer.length === 0
      ) {
        continue;
      }

      if (!connectedQuestion.rendered) {
        return false;
      }

      const answer: Option[] = connectedQuestion.answer;
      const answerGuids = answer.map((option: Option) => option.guid);
      let conditionMet = false;

      switch (condition.operator) {
        case Operators.CONTAINS:
          conditionMet = condition.values.some((value) => answerGuids.includes(value));
          break;

        case Operators.NOT_CONTAINS:
          conditionMet = !condition.values.some((value) => answerGuids.includes(value));
          break;

        case Operators.GREATER_THAN: {
          const numericAnswer = parseFloat(answer[0].text as string);
          const numericValue = parseFloat(condition.values[0]);
          conditionMet =
            !isNaN(numericAnswer) && !isNaN(numericValue) && numericAnswer > numericValue;
          break;
        }

        case Operators.GREATER_EQUAL: {
          const numericAnswerGreaterEqual = parseFloat(answer[0].text as string);
          const numericValueGreaterEqual = parseFloat(condition.values[0]);
          conditionMet =
            !isNaN(numericAnswerGreaterEqual) &&
            !isNaN(numericValueGreaterEqual) &&
            numericAnswerGreaterEqual >= numericValueGreaterEqual;
          break;
        }

        case Operators.LESS_THAN: {
          const numericAnswerLess = parseFloat(answer[0].text as string);
          const numericValueLess = parseFloat(condition.values[0]);
          conditionMet =
            !isNaN(numericAnswerLess) &&
            !isNaN(numericValueLess) &&
            numericAnswerLess < numericValueLess;
          break;
        }

        case Operators.LESS_EQUAL: {
          const numericAnswerLessEqual = parseFloat(answer[0].text as string);
          const numericValueLessEqual = parseFloat(condition.values[0]);
          conditionMet =
            !isNaN(numericAnswerLessEqual) &&
            !isNaN(numericValueLessEqual) &&
            numericAnswerLessEqual <= numericValueLessEqual;
          break;
        }

        default:
          conditionMet = false;
      }

      if (
        condition.connector === ConditionConnector.FIRST ||
        condition.connector === '' ||
        !condition.connector
      ) {
        result = conditionMet;
      } else if (condition.connector === ConditionConnector.AND) {
        result = result !== null ? result && conditionMet : conditionMet;
      } else if (condition.connector === ConditionConnector.OR) {
        result = result !== null ? result || conditionMet : conditionMet;
      }
    }

    return result ?? false;
  };

  const isQuestionRendered = (
    question: Question,
    allQuestions: Question[] = formData.questions.filter((q): q is Question => !isPageBreak(q)),
  ): boolean =>
    question.show_question && checkCondition(question.appeared_conditions, allQuestions);

  const mergeFormulaQuestionsIntoFormItems = (
    formItems: FormItem[],
    updatedQuestions: Question[],
  ): FormItem[] => {
    const updatedByKey = new Map(
      updatedQuestions.map((question) => [question.question_key, question]),
    );
    return formItems.map((item) => {
      if (isPageBreak(item)) {
        return item;
      }
      return updatedByKey.get(item.question_key) ?? item;
    });
  };

  const applyFormulasToQuestions = (questions: FormItem[]): FormItem[] => {
    const answerableQuestions = questions.filter((q): q is Question => !isPageBreak(q));
    const updatedQuestions = applyFormulaCalculations(
      answerableQuestions,
      (question) => isQuestionRendered(question, answerableQuestions),
      getFormulaCalculationOptions(),
    );
    return mergeFormulaQuestionsIntoFormItems(questions, updatedQuestions);
  };

  const withRecalculatedFormulas = (formMetadata: FormMetadata): FormMetadata => ({
    ...formMetadata,
    questions: applyFormulasToQuestions(formMetadata.questions),
  });

  useEffect(() => {
    window.scrollTo(0, 0);
    document.documentElement.scrollLeft = 0;
    document.body.scrollLeft = 0;
    document.getElementById('root')?.scrollTo(0, 0);
  }, [currentPageIndex]);

  useEffect(() => {
    const handleBeforeUnload = (event: BeforeUnloadEvent) => {
      if (isSavingInProgress) {
        event.preventDefault();
        event.returnValue = '';

        datadogLogs.logger.info('User tried to exit page before save', {
          component: 'QuestionnaireForm',
          form_id: getStoredUuidPathSegment(),
        });
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [isSavingInProgress]);

  useEffect(() => {
    if (loadingForm || hasSeededFormulasRef.current || formData.questions.length === 0) {
      return;
    }

    const answerableQuestions = formData.questions.filter((q): q is Question => !isPageBreak(q));
    const updatedQuestions = applyFormulaCalculations(
      answerableQuestions,
      (question) => isQuestionRendered(question, answerableQuestions),
      getFormulaCalculationOptions(),
    );
    const updatedByKey = new Map(
      updatedQuestions.map((question) => [question.question_key, question]),
    );
    const seededQuestions = formData.questions.map((item) => {
      if (isPageBreak(item)) {
        return item;
      }
      return updatedByKey.get(item.question_key) ?? item;
    });

    hasSeededFormulasRef.current = true;

    if (JSON.stringify(seededQuestions) !== JSON.stringify(formData.questions)) {
      setFormData({ ...formData, questions: seededQuestions });
    }
    // Seed formula answers once when the form first loads; avoid re-running on every formData change.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loadingForm]);

  const handlePreviewSubmit = () => {
    setPreviewMessage(null);
    const newErrors = validateMandatoryFormData(formData, language);
    setErrors(newErrors);
    if (Object.keys(newErrors).length > 0) {
      const firstErrorKey = Object.keys(newErrors)[0];
      const el = document.getElementById(firstErrorKey);
      if (el) {
        el.scrollIntoView({ behavior: 'smooth', block: 'center' });
        el.focus?.();
      }
      return;
    }
    setShowDemoThankYou(true);
  };

  const handleNextPage = () => {
    // Validate only the questions on the current page, not future pages.
    // Off-page questions retain rendered=true from when their page was shown
    // (needed for cross-page conditions), so a full-form validation would
    // incorrectly block navigation with errors from pages not yet reached.
    // Pass the full question list so DONORNAME can still match the top-of-form name.
    const currentPageErrors = validateMandatoryFormData(
      {
        ...formData,
        questions: questionsToRender as FormItem[],
      },
      language,
      formData.questions,
    );
    setErrors(currentPageErrors);
    if (Object.keys(currentPageErrors).length > 0) {
      const firstErrorKey = Object.keys(currentPageErrors)[0];
      const el = document.getElementById(firstErrorKey);
      if (el) {
        el.scrollIntoView({ behavior: 'smooth', block: 'center' });
        el.focus?.();
      }
      return;
    }
    setCurrentPageIndex((i) => Math.min(i + 1, totalPages - 1));
  };

  const renderPaginationButton = (label: string, onClick: () => void, extraClassName?: string) => (
    <button
      type='button'
      className={`pagination-btn ${extraClassName ?? ''}`.trim()}
      onClick={onClick}
    >
      {label}
    </button>
  );

  const handleChange = async (questionKey: string, answer: Option[]) => {
    if (isReadOnlyRef.current) return;
    const updatedFormData = withRecalculatedFormulas({
      ...formDataRef.current,
      questions: formDataRef.current.questions.map((question) =>
        !isPageBreak(question) && question.question_key === questionKey
          ? { ...question, answer: answer }
          : question,
      ),
    });

    formDataRef.current = updatedFormData;
    setFormData(updatedFormData);

    if (isPreview) {
      return;
    }

    setSavingQuestions((prev) => ({ ...prev, [questionKey]: true }));

    const uuid = getStoredUuidPathSegment();

    try {
      await updateFormData(formDataRef.current, translation, logout, uuid);
      setErrors((prev) => {
        const newErrors = { ...prev };
        if (isVolunteerNameQuestionKey(questionKey)) {
          const mismatch = getVolunteerNameMismatchError(
            updatedFormData.questions,
            String(answer[0]?.text ?? ''),
            language,
          );
          if (mismatch) {
            newErrors[questionKey] = mismatch;
          } else {
            delete newErrors[questionKey];
          }
        } else {
          delete newErrors[questionKey];
        }
        return newErrors;
      });
      datadogLogs.logger.info(`Question key ${questionKey} was updated in form ${uuid}`, {
        component: 'QuestionnaireForm',
        form_id: getStoredUuidPathSegment(),
        answer: answer.map((opt) => opt.text).join(', '),
      });
    } catch (error: unknown) {
      if (error instanceof FormValidationError) {
        const errorKey = error.errorQuestionKey;
        const el = document.getElementById(errorKey);

        setErrors((prev) => ({
          ...prev,
          [errorKey]: getValidationMessage(language, 'error_question'),
        }));

        if (el) {
          el.scrollIntoView({ behavior: 'smooth', block: 'center' });
          el.focus?.();
        }
      } else {
        setErrors((prev) => ({
          ...prev,
          [questionKey]: getValidationMessage(language, 'general_submit_error'),
        }));
      }
    } finally {
      setSavingQuestions((prev) => ({ ...prev, [questionKey]: false }));
    }
  };

  const renderQuestions = (disabled = formDisabled, questionsOverride?: Question[]) => {
    const toRender = questionsOverride ?? questionsToRender;

    if (!questionsOverride) {
      // Only refresh rendered state for the current page during normal page rendering.
      // Off-page questions keep their rendered state from when their page was shown,
      // so cross-page appeared_conditions evaluate correctly.
      const toRenderGuids = new Set(toRender.map((q) => q.guid));
      formData.questions.forEach((q) => {
        if (isPageBreak(q)) return;
        if (toRenderGuids.has(q.guid)) {
          q.rendered = q.show_question && checkCondition(q.appeared_conditions);
        }
      });
    }

    return toRender.map((question: Question) => {
      const shouldRenderQuestion =
        question.show_question && checkCondition(question.appeared_conditions);
      const mandatory = question.mandatory && checkCondition(question.question_conditions);
      question.mandatory = mandatory;
      question.rendered = shouldRenderQuestion;

      if (!shouldRenderQuestion) return null;

      const calculatedValue = question.formula
        ? calculateFormula(
            question.formula,
            formData.questions.filter((q): q is Question => !isPageBreak(q)),
            {
              ...getFormulaCalculationOptions(),
              question_key: question.question_key,
            },
          )
        : null;

      if (question.question_type === QuestionType.HEADER) {
        return (
          <div className='form-headers' key={question.guid}>
            {renderSanitizedHtml(separatePatientIdFromNameLabel(question.text))}
          </div>
        );
      }

      return (
        <div className='question-container' key={question.guid}>
          <label className='question-label'>
            <div
              className='question-label-inner'
              style={{ display: 'flex', alignItems: 'center', gap: '8px' }}
            >
              {mandatory && <span className='mandatory-star'>*</span>}

              <div>{renderSanitizedHtml(question.text)}</div>

              {savingQuestions[question.question_key] && <span className='loader-spinner' />}
            </div>
          </label>

          {question.show_description && question.description && (
            <div className='question-description'>{renderSanitizedHtml(question.description)}</div>
          )}

          {question.question_type === QuestionType.TEXT && (
            <FormInput
              question={question}
              errors={errors}
              setErrors={setErrors}
              handleChange={handleChange}
              calculatedValue={calculatedValue}
              disabled={disabled}
              allQuestions={formData.questions.filter((q): q is Question => !isPageBreak(q))}
            />
          )}
          {question.question_type === QuestionType.SELECT && (
            <FormSelect
              question={question}
              errors={errors}
              setErrors={setErrors}
              handleChange={handleChange}
              disabled={disabled}
            />
          )}

          {question.question_type === QuestionType.CHECKBOX && (
            <FormCheckbox
              question={question}
              handleChange={handleChange}
              errors={errors}
              setErrors={setErrors}
              disabled={disabled}
            />
          )}

          {question.question_type === QuestionType.RADIO && (
            <FormRadioButton
              question={question}
              handleChange={handleChange}
              errors={errors}
              setErrors={setErrors}
              disabled={disabled}
            />
          )}

          {question.question_type === QuestionType.SCALE && (
            <FormScale
              question={question}
              handleChange={handleChange}
              errors={errors}
              setErrors={setErrors}
              disabled={formDisabled}
            />
          )}
        </div>
      );
    });
  };

  const allQuestionsPreview = useMemo(
    () => renderQuestions(true, pages.flat()),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [formData.questions, pages],
  );

  if (loadingForm) {
    return (
      <div className='questionnaire-loading-overlay'>
        <div className='questionnaire-spinner'></div>
      </div>
    );
  }

  if (showFinishedFormPage || (formData.form_filled && !showFormAfterFill)) {
    return (
      <div className='finished-form-page'>
        <div>{translation.form_thank_you_title}</div>
        <div>{translation.form_thank_you_subtitle}</div>
      </div>
    );
  }

  return (
    <div className='form-container' dir={formDirection}>
      {isPreview && (
        <>
          <div className='demo-watermark-wrap' aria-hidden='true'>
            <svg className='demo-watermark-svg' xmlns='http://www.w3.org/2000/svg'>
              <defs>
                <pattern
                  id='demo-questionnaire-pattern'
                  patternUnits='userSpaceOnUse'
                  width='220'
                  height='70'
                >
                  <text
                    className='demo-watermark-pattern-text'
                    x='110'
                    y='38'
                    textAnchor='middle'
                    transform='rotate(-35 110 35)'
                  >
                    {translation.demo_watermark_text}
                  </text>
                </pattern>
              </defs>
              <rect width='100%' height='100%' fill='url(#demo-questionnaire-pattern)' />
            </svg>
          </div>
          <div className='preview-banner'>{translation.demo_banner_text}</div>
        </>
      )}
      {renderQuestions(formDisabled)}
      {isMultiPage && (
        <div className='pagination-progress' role='status' aria-live='polite'>
          {translation.page_label} {currentPageIndex + 1} {translation.of_label} {totalPages}
        </div>
      )}
      {isMultiPage ? (
        <div className='pagination-nav submit-wrapper'>
          <div className='pagination-nav-cell'>
            {currentPageIndex > 0 &&
              renderPaginationButton(
                translation.pagination_prev_label,
                () => setCurrentPageIndex((i) => Math.max(0, i - 1)),
                'pagination-back',
              )}
          </div>
          <div className='pagination-nav-cell'>
            {currentPageIndex === totalPages - 1 &&
              (isPreview ? (
                <div className='submit-wrapper'>
                  <button type='button' className='submit-btn' onClick={handlePreviewSubmit}>
                    {translation.submit_label}
                  </button>
                  {previewMessage && (
                    <p className='preview-message' role='alert' style={{ marginTop: '0.5rem' }}>
                      {previewMessage}
                    </p>
                  )}
                </div>
              ) : (
                <FormSubmitButton
                  isLoading={isLoading}
                  setIsLoading={setIsLoading}
                  errors={errors}
                  setErrors={setErrors}
                  formData={formData}
                  setShowFinishedFormPage={setShowFinishedFormPage}
                  disabled={formDisabled}
                  logout={logout}
                  showPreview={showPreview}
                  setShowPreview={setShowPreview}
                  previewContent={allQuestionsPreview}
                />
              ))}
          </div>
          <div className='pagination-nav-cell'>
            {currentPageIndex < totalPages - 1 &&
              renderPaginationButton(translation.pagination_next_label, handleNextPage)}
          </div>
        </div>
      ) : isPreview ? (
        <div className='submit-wrapper'>
          <button type='button' className='submit-btn' onClick={handlePreviewSubmit}>
            {translation.submit_label}
          </button>
          {previewMessage && (
            <p className='preview-message' role='alert' style={{ marginTop: '0.5rem' }}>
              {previewMessage}
            </p>
          )}
        </div>
      ) : (
        <FormSubmitButton
          isLoading={isLoading}
          setIsLoading={setIsLoading}
          errors={errors}
          setErrors={setErrors}
          formData={formData}
          setShowFinishedFormPage={setShowFinishedFormPage}
          disabled={formDisabled}
          logout={logout}
          showPreview={showPreview}
          setShowPreview={setShowPreview}
          previewContent={allQuestionsPreview}
        />
      )}
      {showDemoThankYou && (
        <div
          className='demo-thank-you-overlay'
          role='dialog'
          aria-modal='true'
          aria-labelledby='demo-thank-you-title'
        >
          <div className='demo-thank-you-modal'>
            <h2 id='demo-thank-you-title' className='demo-thank-you-title'>
              {translation.demo_thank_you_title}
            </h2>
            <button
              type='button'
              className='demo-thank-you-close'
              onClick={() => setShowDemoThankYou(false)}
              aria-label={translation.close_label}
            >
              {translation.close_label}
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default QuestionnaireForm;
